import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import WithListConfig from "../../hocs/WithListConfig";
import DynamicTable from "../DynamicTable";
import fetcher from "../../services/fetcher";
import ListContext from "./ListContext";
import AppContext from "../../context";
import ButtonsBar from "../ButtonsBar";
import { objectToUrlParams, searchFields } from "../../services/commonServices";
import parse from "html-react-parser";
import { TO_UPPER_CASE } from "../../config";
import { useHistory } from "react-router-dom";
import { useTabConfig } from "../../hooks/useTabConfig";
import { validateConditions } from "../../utils";
import { useTranslation } from "react-i18next";
import { readSessionStorage } from "../../services/sessionStorageManagement";

const getInitialParams = (props) => {
  const { params = {}, filtersData = {} } = props;
  const urlSearchParams = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );
  // Leer page de la URL primero, si no existe usar 1
  const pageFromUrl = urlSearchParams.page || 1;
  return { ...params, cpages: 1, page: pageFromUrl, ...filtersData, ...urlSearchParams };
};

const List = (props) => {
  const {
    id,
    config,
    withoutPlusBtn,
    withoutButtonsBar,
    withoutFilters,
    withoutHeader,
    withoutSearcher,
    withBtnExcel,
    customName,
    addBtn,
    withoutMargin,
    className,
    getItems,
    formContext,
    tab,
  } = props;
  const {
    config: { globalParams },
  } = useContext(AppContext);
  const { config: { routes } = {} } = useContext(AppContext);
  const [t] = useTranslation("app");
  const fetchTimeout = useRef(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [urlParams, setUrlParams] = useState(getInitialParams(props));
  const initialPage = parseInt(urlParams.page) || 1;
  const [page, setPage] = useState(initialPage);
  const [advancedSearchForm, setAdvancedSearchForm] = useState(null);
  const isPageChangeFromUrl = useRef(false);

  // Sincronizar page con urlParams cuando la URL cambia (navegación directa)
  useEffect(() => {
    const urlPage = parseInt(urlParams.page) || 1;
    const currentPage = parseInt(page) || 1;
    if (urlPage !== currentPage) {
      isPageChangeFromUrl.current = true;
      setPage(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.page]);
  const [QuantityRecord, setQuantityRecord] = useState(10);
  const [orderFields, setOrderFields] = useState("");
  const [keywords, setKeywords] = useState(() => {
    const urlSearchParams = Object.fromEntries(new URLSearchParams(window.location.search));
    const searchId = config?.["__search_id"];
    return urlSearchParams[searchId] || urlSearchParams.q || "";
  });
  const [orderType, setOrderType] = useState("desc");
  const [fields, setFields] = useState(() => {
    const sf = config?.["search_fields"];
    if (!sf) return "";
    if (typeof sf === "string") return sf;
    if (Array.isArray(sf)) return sf.map((f) => f.field).join(",");
    return "";
  });


  const [checkFromInput, setCheckFromInputs] = useState([]);
  const [currentIds, setCurrentIds] = useState([]);
  const [selectedPage, setSelectedPage] = useState([]);
  const [headerData, setHeaderData] = useState(null);
  const [firstItemInfo, setFirstItemInfo] = useState({});
  const history = useHistory();
  const { getTabData } = useTabConfig(tab, data, routes);
  const [newConfig, setNewConfig] = useState(config);

  useEffect(() => {
    const allIds = data.items?.map((item) => item.pk);
    allIds && setCurrentIds(allIds);
    return () => {
      clearTimeout(fetchTimeout.current);
    };
  }, [data]);

  const handleToSaveAllCheckInputs = (id) => {
    setCheckFromInputs((prev) => {
      if (!prev.length > 0) return [id];
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
    // const newArr = id.filter(value => checkFromInput.includes(value))
  };

  const getListData = useCallback(() => {
    setLoading(true);
    if (getItems) {
      const items = getItems();
      let headerOptions = null;
      if (items && Array.isArray(items.items) && items.items.length) {
        headerOptions = searchFields(
          [["header__", ""], "list_title"],
          items.items[0],
        );
        setFirstItemInfo(items.items[0]);
      }
      if (headerOptions && Object.keys(headerOptions).length)
        setHeaderData(headerOptions);
      setLoading(false);
      return setData(items);
    }
    if (config.uri === "/inventory/" && "asset" in globalParams) {
      delete globalParams["asset"];
      console.log("Aplicando guarrada de borrar asset...");
    }

    if (tab) {
      getTabData().then((res) => {
        setNewConfig(res ? { ...res.config, tabType: res.tabType } : config);
        const { read, advanced_search } = res
          ? res.config.actions
          : config.actions;
        const tabSearchFields = res?.config?.["search_fields"];
        const resolvedFields = tabSearchFields
          ? (typeof tabSearchFields === "string"
              ? tabSearchFields
              : Array.isArray(tabSearchFields)
              ? tabSearchFields.map((f) => f.field).join(",")
              : null)
          : null;
        fetchTabData(read, advanced_search, resolvedFields);
      });
      return;
    }
    const { read, advanced_search } = config.actions;
    fetchTabData(read, advanced_search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedSearchForm, globalParams, urlParams]);

  const fetchTabData = (read, advanced_search, overrideFields = null) => {
    const isAdvancedSeach = Object.keys(advancedSearchForm || {}).length;
    if (!read) return;
    if (urlParams["q"] && urlParams["q"].length <= 2) return;

    const currentSearchParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    const newSearchParams = { ...currentSearchParams };
    for (const [key, value] of Object.entries(urlParams)) {
      if (key === "q" || config["__filters_names"]?.includes(key)) {
        const paramKey = key === "q" ? config["__search_id"] : key;
        if (value !== "" && value !== null && value !== undefined) {
          newSearchParams[paramKey] = value;
        } else {
          delete newSearchParams[paramKey];
        }
      }
    }

    if (urlParams.page) {
      newSearchParams.page = urlParams.page;
    }

    // No pushear search params si estamos en una ruta con cache (página de detalle)
    const currentPath = window.location.pathname;
    const normalizedPath = currentPath.endsWith('/') ? currentPath : currentPath + '/';
    let customRoutes = [];
    try {
      customRoutes = JSON.parse(readSessionStorage('customRoutes', '[]'));
    } catch (e) {
      // Ignorar errores de parsing
    }
    const isCachedRoute = customRoutes.some(route =>
      route && typeof route === 'string' && normalizedPath.startsWith(route)
    );

    if (!isCachedRoute) {
      history.push({
        search: objectToUrlParams(newSearchParams),
      });
    }

    const allParams = {
      ...globalParams,
      ...urlParams,
      ...newSearchParams,
      ...(overrideFields !== null ? { fields: overrideFields } : {}),
    };

    fetcher({
      path: isAdvancedSeach ? advanced_search.uri : read.uri,
      urlParams: allParams,
      body: isAdvancedSeach ? advancedSearchForm : undefined,
      method: isAdvancedSeach ? advanced_search.method : read.method,
    })
      .then((res) => {
        if (Array.isArray(res) || "error" in res) setAdvancedSearchForm(null);
        let headerOptions = null;
        if (res && Array.isArray(res.items) && res.items.length) {
          headerOptions = searchFields(
            [["header__", ""], "list_title"],
            res.items[0],
          );
          setFirstItemInfo(res.items[0]);
        }
        if (headerOptions && Object.keys(headerOptions).length)
          setHeaderData(headerOptions);
        return setData(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchData = () => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(getListData, 500);
  };

  const createNewParams = (newPage = 1, newParams = {}) => {
    const params = {
      ...urlParams,
      ...newParams,
      page: newPage,
      nitems: QuantityRecord,
      order: orderFields,
      order_type: orderType,
    };

    params["q"] =
      keywords && TO_UPPER_CASE ? keywords.toUpperCase() : keywords || "";
    params["fields"] = fields;

    setUrlParams(params);
  };

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      createNewParams(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywords, globalParams, advancedSearchForm]);

  useEffect(() => {
    // Si el cambio de página viene de la URL, no actualizar params (evitar ciclo)
    if (isPageChangeFromUrl.current) {
      isPageChangeFromUrl.current = false;
      return;
    }
    
    // Si el cambio viene del usuario (paginación), actualizar params y URL
    if (tab) {
      createNewParams(page, tab.params);
      return;
    }
    createNewParams(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, QuantityRecord, orderFields, orderType, tab]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams]);

  const handleFilterOptions = (paramsToAdd) => {
    createNewParams(1, paramsToAdd);
  };

  const isReadOnlyList = newConfig?.readOnlyList?.conditions
    ? validateConditions(newConfig.readOnlyList.conditions, firstItemInfo || {}, null, globalParams)
    : false;

  const computedConfig = isReadOnlyList ? {
    ...newConfig,
    rows: {
      ...(newConfig.rows || {}),
      actions: false,
    },
    actions: {
      ...(newConfig.actions || {}),
      read: undefined,
    },
    header: {
      ...(newConfig.header || {}),
      edit: false,
      trash: false,
      read: false,
    }
  } : newConfig;

  const renderButtonsBar = () => {
    if (withoutButtonsBar) return null;
    return (
      <ButtonsBar
        id={id}
        config={computedConfig}
        selectedItems={checkFromInput}
        fetchData={fetchData}
        formData={firstItemInfo}
        formContext={formContext}
      />
    );
  };

  const checkAll = (isChecked) => {
    if (isChecked)
      setCheckFromInputs((prev) => {
        setSelectedPage((prev) => {
          if (prev && !prev.length > 0) return [page];
          if (prev.includes(page)) return prev.filter((x) => x !== page);
          return [...prev, page];
        });
        if (!prev.length > 0) return currentIds;
        return [...prev, ...currentIds];
      });
    else setCheckFromInputs([]);
  };

  const deleteAdvFilterItem = ({ name }) => {
    if (!advancedSearchForm) return;
    const currentAdvFilters = { ...advancedSearchForm };
    delete currentAdvFilters[name];
    delete currentAdvFilters[`${name}__init`];
    delete currentAdvFilters[`${name}__finish`];
    setAdvancedSearchForm(currentAdvFilters);
  };

  const renderHeader = () => {
    if (withoutHeader) return null;
    return headerData ? (
      <div className={headerData.class}>{parse(headerData.text || "")}</div>
    ) : null;
  };

  // Verificar si el config tiene error 403
  if (config && config.error && config.status === 403) {
    return (
      <div className="text-center p-5">
        <h3 className="text-danger">{t('error404')}</h3>
      </div>
    );
  }

  // if (loading) return t('fetchingConfig');
  return (
    <div className={`ListComponent ${className}`}>
      {renderButtonsBar()}
      {renderHeader()}
      <ListContext.Provider
        value={{
          config,
          page,
          setPage,
          QuantityRecord,
          setQuantityRecord,
          setOrderFields,
          orderFields,
          orderType,
          setOrderType,
          handleToSaveAllCheckInputs,
          checkFromInput,
          checkAll,
          currentIds,
          selectedPage,
          urlParams,
          advancedSearchForm,
          setAdvancedSearchForm,
        }}
      >
        {
          <DynamicTable
            id={id}
            setKeywords={setKeywords}
            setFields={setFields}
            config={computedConfig}
            data={data}
            fetchData={fetchData}
            page={parseInt(urlParams?.page) || page || 1}
            onChangeFilters={handleFilterOptions}
            onDeleteAdvFilter={deleteAdvFilterItem}
            withoutFilters={withoutFilters}
            withoutSearcher={withoutSearcher}
            withBtnExcel={withBtnExcel}
            withoutPlusBtn={withoutPlusBtn || isReadOnlyList}
            customName={headerData?.list_title || customName}
            addBtn={addBtn}
            loading={loading}
            globalParams={globalParams}
            withoutMargin={withoutMargin}
            withoutHeader={withoutHeader}
            enableDragAndDrop={newConfig.tabType === "document-gallery"}
          />
        }
      </ListContext.Provider>
    </div>
  );
};

List.propTypes = {
  id: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  params: PropTypes.object,
  withoutButtonsBar: PropTypes.bool,
  withoutFilters: PropTypes.bool,
  withoutMargin: PropTypes.bool,
  withoutPlusBtn: PropTypes.bool,
  withoutHeader: PropTypes.bool,
  withBtnExcel: PropTypes.bool,
  withoutSearcher: PropTypes.bool,
  customName: PropTypes.string,
  addBtn: PropTypes.bool,
  className: PropTypes.string,
  formContext: PropTypes.object,
  tab: PropTypes.object,
};

List.defaultProps = {
  params: {},
};

export default memo(WithListConfig(List));
